import { MouseEvent, ReactNode, useState } from 'react';

import AddchartIcon from '@mui/icons-material/Addchart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PaymentsIcon from '@mui/icons-material/Payments';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';

import Loader from 'src/common/components/Loader';
import { DASHBOARD_GRID_ROW_HEIGHT } from 'src/common/components/dataGrid/useDashboardGridHeight';
import { IEdition } from 'src/interfaces/Edition';
import EditionCompetitionsModal from 'src/pages/Dashboard/Editions/modals/EditionCompetitionsModal';
import EditionTotalsModal from 'src/pages/Dashboard/Editions/modals/EditionTotalsModal';

export function EditionPrimaryRowBand({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minHeight: DASHBOARD_GRID_ROW_HEIGHT,
        height: '100%',
      }}
    >
      {children}
    </Box>
  );
}

type EditionYearCellProps = {
  edition: IEdition;
  isLoading?: boolean;
};

export function EditionYearCell({ edition, isLoading }: EditionYearCellProps) {
  const { t } = useTranslation();
  const [competitionsOpen, setCompetitionsOpen] = useState(false);
  const [totalsOpen, setTotalsOpen] = useState(false);

  if (isLoading) {
    return <Loader isLoading height="24px" />;
  }

  return (
    <>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, width: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        <Typography variant="body2" component="span" sx={{ minWidth: 0 }}>
          {edition.year}
        </Typography>
        <Tooltip title={t('editions.competitions')}>
          <IconButton
            size="small"
            aria-label={t('editions.competitions')}
            onClick={() => setCompetitionsOpen(true)}
          >
            <EmojiEventsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('editions.totals')}>
          <IconButton
            size="small"
            aria-label={t('editions.totals')}
            onClick={() => setTotalsOpen(true)}
          >
            <PaymentsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <EditionCompetitionsModal
        edition={edition}
        open={competitionsOpen}
        onClose={() => setCompetitionsOpen(false)}
      />
      <EditionTotalsModal edition={edition} open={totalsOpen} onClose={() => setTotalsOpen(false)} />
    </>
  );
}

type EditionRowActionsProps = {
  edition: IEdition;
  activeEditionId?: string;
  onDeleteEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onEditEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onSaveEdition: (editedEdition: IEdition, id?: string) => void;
  onSaveStatistics: (editionId: string, e?: MouseEvent<unknown>) => void;
  onToggleActiveEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
};

export function EditionRowActionsMenu({
  edition,
  activeEditionId,
  onDeleteEdition,
  onEditEdition,
  onSaveEdition,
  onSaveStatistics,
  onToggleActiveEdition,
}: EditionRowActionsProps) {
  const { t } = useTranslation();
  const actionsMenu = usePopupState({ variant: 'popover', popupId: `edition-actions-${edition.id}` });

  const isActive = edition.id === activeEditionId;

  const closeAnd = (fn: () => void) => () => {
    fn();
    actionsMenu.close();
  };

  return (
    <>
      <IconButton
        size="small"
        {...bindTrigger(actionsMenu)}
        aria-label={t('editions.optionsLabel')}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu {...bindMenu(actionsMenu)}>
        <MenuItem onClick={closeAnd(() => onEditEdition(edition.id))}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('editions.editEdition')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={closeAnd(() => onDeleteEdition(edition.id))}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteForeverIcon color="error" fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('editions.deleteEdition')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={closeAnd(() =>
            onSaveEdition({ ...edition, canShowResults: !edition.canShowResults }, edition.id),
          )}
        >
          <ListItemIcon>
            {edition.canShowResults ? (
              <AnalyticsIcon fontSize="small" color="primary" />
            ) : (
              <AnalyticsOutlinedIcon fontSize="small" color="warning" />
            )}
          </ListItemIcon>
          <ListItemText>
            {edition.canShowResults ? t('editions.hideResults') : t('editions.showResults')}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={closeAnd(() => onSaveStatistics(edition.id))}>
          <ListItemIcon>
            <AddchartIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('editions.saveStatistics')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeAnd(() => onToggleActiveEdition(edition.id))}>
          <ListItemIcon>
            {isActive ? (
              <ToggleOnIcon fontSize="small" color="primary" />
            ) : (
              <ToggleOffOutlinedIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {isActive ? t('editions.deactivateEdition') : t('editions.activateEdition')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
